import React, { useEffect, useMemo, useState } from "react";
import { formatGestureName, getEntityDocumentId, getRequestDocumentId, buildRequestPlaceholder, useAppStore } from "../store/useAppStore";
import UniversalModal from "./items/UniversalModal";

const requestLabel = (request) => {
  const lemma = request?.lemma || "—";
  const documentId = getRequestDocumentId(request);
  return documentId ? `${lemma} (${documentId})` : lemma;
};

const signLabel = (sign) => {
  const name = sign?.name || sign?.lemma || "—";
  const documentId = getEntityDocumentId(sign);
  return documentId ? `${name} (${documentId})` : name;
};

const RequestMergeModal = ({ isOpen, request, signRequests, onClose, onSuccess }) => {
  const mergeSignRequestWithRequest = useAppStore((state) => state.mergeSignRequestWithRequest);
  const mergeSignRequestWithSign = useAppStore((state) => state.mergeSignRequestWithSign);
  const signRequestsMutationLoading = useAppStore((state) => state.signRequestsMutationLoading);
  const signRequestsMutationError = useAppStore((state) => state.signRequestsMutationError);
  const fetchSigns = useAppStore((state) => state.fetchSigns);
  const signs = useAppStore((state) => state.signs);
  const signsLoading = useAppStore((state) => state.signsLoading);
  const signsError = useAppStore((state) => state.signsError);

  const [mergeMode, setMergeMode] = useState("request");
  const [partnerDocumentId, setPartnerDocumentId] = useState("");
  const [partnerSearch, setPartnerSearch] = useState("");
  const [mainRequestSide, setMainRequestSide] = useState("current");
  const [keepLemmaSide, setKeepLemmaSide] = useState("current");
  const [signSearchInput, setSignSearchInput] = useState("");
  const [signSearchTerm, setSignSearchTerm] = useState("");
  const [selectedSignDocumentId, setSelectedSignDocumentId] = useState("");

  useEffect(() => {
    if (!isOpen) return undefined;

    setMergeMode("request");
    setPartnerDocumentId("");
    setPartnerSearch("");
    setMainRequestSide("current");
    setKeepLemmaSide("current");
    setSignSearchInput("");
    setSignSearchTerm("");
    setSelectedSignDocumentId("");

    return undefined;
  }, [isOpen, request?.documentId]);

  useEffect(() => {
    if (!isOpen || mergeMode !== "sign") return undefined;

    const timer = setTimeout(() => {
      setSignSearchTerm(signSearchInput.trim());
    }, 350);

    return () => clearTimeout(timer);
  }, [isOpen, mergeMode, signSearchInput]);

  useEffect(() => {
    if (!isOpen || mergeMode !== "sign") return undefined;
    fetchSigns(1, 12, signSearchTerm, "name");
    return undefined;
  }, [isOpen, mergeMode, signSearchTerm, fetchSigns]);

  const partnerOptions = useMemo(() => {
    const currentDocumentId = getRequestDocumentId(request);
    return (signRequests || [])
      .filter((item) => String(getRequestDocumentId(item)) !== String(currentDocumentId))
      .filter((item) => {
        const query = partnerSearch.trim().toLowerCase();
        if (!query) return true;
        const lemma = String(item?.lemma || "").toLowerCase();
        const documentId = String(getRequestDocumentId(item) || "").toLowerCase();
        return lemma.includes(query) || documentId.includes(query);
      })
      .sort((a, b) => String(a?.lemma || "").localeCompare(String(b?.lemma || ""), "ru", { sensitivity: "base" }));
  }, [signRequests, request, partnerSearch]);

  const partnerRequest = useMemo(() => {
    return partnerOptions.find((item) => String(getRequestDocumentId(item)) === String(partnerDocumentId)) || null;
  }, [partnerOptions, partnerDocumentId]);

  const selectedSign = useMemo(() => {
    return signs.find((item) => String(getEntityDocumentId(item)) === String(selectedSignDocumentId)) || null;
  }, [signs, selectedSignDocumentId]);

  const mergePreview = useMemo(() => {
    if (!request) return "";
    const requestPlaceholder = buildRequestPlaceholder(request) || "p:?";

    if (mergeMode === "sign") {
      if (!selectedSign) return `В переводах ${requestPlaceholder} будет заменён на documentId жеста.`;
      return `В переводах ${requestPlaceholder} → ${getEntityDocumentId(selectedSign)}. Заявка станет «Добавлено».`;
    }

    if (!partnerRequest) {
      return "Выберите вторую заявку для слияния.";
    }

    const main = mainRequestSide === "current" ? request : partnerRequest;
    const source = mainRequestSide === "current" ? partnerRequest : request;
    const lemma = keepLemmaSide === "current" ? request.lemma : partnerRequest.lemma;

    return `Основная: ${getRequestDocumentId(main)}. Удаляется: ${getRequestDocumentId(source)}. В переводах ${buildRequestPlaceholder(source)} → ${buildRequestPlaceholder(main)}. Итоговое название: «${lemma || "—"}».`;
  }, [mergeMode, request, partnerRequest, mainRequestSide, keepLemmaSide, selectedSign]);

  const canSubmit = useMemo(() => {
    if (!request || signRequestsMutationLoading) return false;
    if (mergeMode === "sign") return Boolean(selectedSign);
    return Boolean(partnerRequest);
  }, [request, mergeMode, partnerRequest, selectedSign, signRequestsMutationLoading]);

  const handleSubmit = async () => {
    if (!request || !canSubmit) return;

    let result = { ok: false, updatedTranslations: 0 };

    if (mergeMode === "sign") {
      const confirmed = window.confirm(
        `Слить заявку «${request.lemma || request.id}» с жестом «${signLabel(selectedSign)}»?\n\n${mergePreview}`,
      );
      if (!confirmed) return;

      result = await mergeSignRequestWithSign({ request, sign: selectedSign });
    } else {
      const main = mainRequestSide === "current" ? request : partnerRequest;
      const source = mainRequestSide === "current" ? partnerRequest : request;
      const lemma = keepLemmaSide === "current" ? request.lemma : partnerRequest.lemma;

      const confirmed = window.confirm(
        `Объединить заявки?\n\n${mergePreview}\n\nБудут обновлены все переводы с ${buildRequestPlaceholder(source)}.`,
      );
      if (!confirmed) return;

      result = await mergeSignRequestWithRequest({
        sourceRequest: source,
        targetRequest: main,
        lemma,
      });
    }

    if (!result?.ok) return;

    onSuccess?.({
      mode: mergeMode,
      updatedTranslations: result.updatedTranslations || 0,
    });
    onClose?.();
  };

  if (!request) return null;

  return (
    <UniversalModal
      isOpen={isOpen}
      title={`Merge: ${request.lemma || getRequestDocumentId(request) || "—"}`}
      onClose={onClose}
      footer={(
        <>
          <button className="e-btn" type="button" onClick={onClose} disabled={signRequestsMutationLoading}>
            Отмена
          </button>
          <button className="e-btn" type="button" onClick={handleSubmit} disabled={!canSubmit}>
            {signRequestsMutationLoading ? "Выполняется…" : "Выполнить merge"}
          </button>
        </>
      )}
    >
      <div className="request-merge-mode-switch">
        <button
          type="button"
          className={`request-merge-mode-btn ${mergeMode === "request" ? "active" : ""}`}
          onClick={() => setMergeMode("request")}
        >
          С заявкой
        </button>
        <button
          type="button"
          className={`request-merge-mode-btn ${mergeMode === "sign" ? "active" : ""}`}
          onClick={() => setMergeMode("sign")}
        >
          С жестом
        </button>
      </div>

      <p className="request-merge-current">
        <strong>Текущая заявка:</strong> {requestLabel(request)}
        <span className="request-merge-code">placeholder: {buildRequestPlaceholder(request) || "—"}</span>
      </p>

      {mergeMode === "request" ? (
        <div className="request-merge-form">
          <label className="request-merge-field" htmlFor="request-merge-partner-search">
            <span>Вторая заявка</span>
            <input
              id="request-merge-partner-search"
              type="text"
              value={partnerSearch}
              onChange={(event) => setPartnerSearch(event.target.value)}
              placeholder="Поиск по lemma или documentId"
            />
          </label>

          <label className="request-merge-field" htmlFor="request-merge-partner">
            <span>Выбор заявки</span>
            <select
              id="request-merge-partner"
              value={partnerDocumentId}
              onChange={(event) => setPartnerDocumentId(event.target.value)}
            >
              <option value="">— выберите —</option>
              {partnerOptions.map((item) => (
                <option key={getRequestDocumentId(item)} value={getRequestDocumentId(item)}>
                  {requestLabel(item)}
                </option>
              ))}
            </select>
          </label>

          <fieldset className="request-merge-fieldset">
            <legend>Основная заявка (остаётся в базе)</legend>
            <label className="request-merge-radio">
              <input
                type="radio"
                name="merge-main-request"
                checked={mainRequestSide === "current"}
                onChange={() => setMainRequestSide("current")}
              />
              <span>{requestLabel(request)}</span>
            </label>
            <label className="request-merge-radio">
              <input
                type="radio"
                name="merge-main-request"
                checked={mainRequestSide === "partner"}
                onChange={() => setMainRequestSide("partner")}
                disabled={!partnerRequest}
              />
              <span>{partnerRequest ? requestLabel(partnerRequest) : "—"}</span>
            </label>
          </fieldset>

          <fieldset className="request-merge-fieldset">
            <legend>Название после слияния</legend>
            <label className="request-merge-radio">
              <input
                type="radio"
                name="merge-keep-lemma"
                checked={keepLemmaSide === "current"}
                onChange={() => setKeepLemmaSide("current")}
              />
              <span>{request.lemma || "—"}</span>
            </label>
            <label className="request-merge-radio">
              <input
                type="radio"
                name="merge-keep-lemma"
                checked={keepLemmaSide === "partner"}
                onChange={() => setKeepLemmaSide("partner")}
                disabled={!partnerRequest}
              />
              <span>{partnerRequest?.lemma || "—"}</span>
            </label>
          </fieldset>
        </div>
      ) : (
        <div className="request-merge-form">
          <label className="request-merge-field" htmlFor="request-merge-sign-search">
            <span>Поиск жеста</span>
            <input
              id="request-merge-sign-search"
              type="text"
              value={signSearchInput}
              onChange={(event) => setSignSearchInput(event.target.value)}
              placeholder="name"
            />
          </label>
          <p className="request-merge-hint">Запрос: {formatGestureName(signSearchInput) || "—"}</p>

          {signsLoading ? (
            <p className="request-merge-hint"><i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i> Поиск жестов…</p>
          ) : null}
          {signsError ? (
            <p className="requests-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {signsError}</p>
          ) : null}

          <div className="request-merge-sign-list">
            {signs.length ? signs.map((sign) => {
              const signDocumentId = getEntityDocumentId(sign);
              const active = String(signDocumentId) === String(selectedSignDocumentId);
              return (
                <button
                  key={signDocumentId || sign?.id}
                  type="button"
                  className={`request-merge-sign-item ${active ? "active" : ""}`}
                  onClick={() => setSelectedSignDocumentId(String(signDocumentId))}
                >
                  <span>{signLabel(sign)}</span>
                  {active ? <i className="fa-solid fa-check" aria-hidden="true"></i> : null}
                </button>
              );
            }) : (
              <p className="request-merge-hint">Жесты не найдены</p>
            )}
          </div>
        </div>
      )}

      <p className="request-merge-preview">{mergePreview}</p>

      {signRequestsMutationError ? (
        <p className="requests-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {signRequestsMutationError}</p>
      ) : null}
    </UniversalModal>
  );
};

export default RequestMergeModal;
